import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query'

import { apiClient } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

/**
 * Le client du back-office.
 *
 * Deux choses sont ajoutées à chaque appel, et l'oubli de l'une ou l'autre est
 * la source d'erreur la plus fréquente de ce projet :
 *
 * * le **jeton** (renouvelé à la volée par `accessToken()`) ;
 * * l'**en-tête `X-Event-Id`** — un rôle attribué sur une édition n'accorde
 *   rien sans lui. Un endpoint répond alors 403 sans expliquer pourquoi, et on
 *   cherche du côté des permissions au lieu de l'en-tête.
 *
 * Passer par ces deux helpers plutôt que par `apiClient` directement garantit
 * qu'aucun écran ne peut oublier ni l'un ni l'autre.
 */
async function authorized(eventId: string | undefined) {
  return { token: await accessToken(), ...(eventId ? { eventId } : {}) }
}

export function useAdminQuery<T>(
  key: QueryKey,
  path: string,
  eventId: string | undefined,
  options: {
    enabled?: boolean
    // Un écran qui suit un travail en cours se relit tout seul, et seulement
    // tant qu'il y a quelque chose à suivre (fonction de la dernière réponse).
    refetchInterval?: UseQueryOptions<T, Error, T, QueryKey>['refetchInterval']
    retry?: UseQueryOptions<T, Error, T, QueryKey>['retry']
  } = {},
) {
  return useQuery({
    queryKey: key,
    queryFn: async () => apiClient.get<T>(path, await authorized(eventId)),
    enabled: (options.enabled ?? true) && Boolean(eventId),
    ...(options.refetchInterval !== undefined ? { refetchInterval: options.refetchInterval } : {}),
    ...(options.retry !== undefined ? { retry: options.retry } : {}),
  })
}

type Method = 'post' | 'patch' | 'put' | 'delete'

/**
 * Une écriture, suivie de l'invalidation des listes concernées.
 *
 * `invalidate` prend les préfixes de clés à rafraîchir. On invalide plutôt
 * qu'on ne met à jour le cache à la main : le serveur peut avoir normalisé,
 * complété ou refusé une partie de ce qu'on a envoyé, et une mise à jour
 * optimiste afficherait alors autre chose que ce qui est réellement stocké.
 */
export function useAdminMutation<TResult, TVariables = unknown>(
  method: Method,
  path: (variables: TVariables) => string,
  eventId: string | undefined,
  invalidate: QueryKey[] = [],
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const options = await authorized(eventId)
      const url = path(variables)
      if (method === 'delete') return apiClient.delete<TResult>(url, options)
      return apiClient[method]<TResult>(url, variables, options)
    },
    onSuccess: () => {
      for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: key })
    },
  })
}

/**
 * Une collection entière, page après page.
 *
 * Le serveur plafonne une page à 200 lignes — et le backlog de préparation en
 * compte plus de quatre cents. Un écran qui demanderait « page_size=200 » et
 * s'arrêterait là n'afficherait que la moitié du travail **sans rien signaler** :
 * ni message, ni compteur, ni bouton « suivant ». C'est la panne la plus
 * traîtresse qui soit, parce qu'elle ressemble à une liste complète.
 *
 * Le kanban a de toute façon besoin de tout : ses colonnes se remplissent en
 * répartissant l'ensemble, et une page manquante viderait une colonne entière.
 *
 * La garde à cinquante pages n'est pas de la prudence décorative : une réponse
 * mal formée qui rendrait toujours la même page ferait tourner cette boucle
 * indéfiniment, et l'onglet se figerait sans qu'on comprenne pourquoi.
 */
export function useAdminPagedQuery<T>(
  key: QueryKey,
  path: (page: number) => string,
  eventId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const auth = await authorized(eventId)
      const rows: T[] = []
      let page = 1
      for (;;) {
        const body = await apiClient.get<{
          items: T[]
          pages: number
          total: number
        }>(path(page), auth)
        rows.push(...body.items)
        if (page >= body.pages || body.items.length === 0 || page > 50) return rows
        page += 1
      }
    },
    enabled: (options.enabled ?? true) && Boolean(eventId),
  })
}
